<template>
  <div class="transcript-viewer">
    <div v-if="transcriptUrl && isLoading" class="transcript-state">
      <LoadingBars size="sm" text="Loading transcript..." />
    </div>

    <div v-else-if="transcriptUrl && error" class="transcript-state transcript-error-state">
      <p class="error-message">{{ errorMessage }}</p>
      <button type="button" class="btn btn-primary btn-sm" @click="handleRetry">Retry</button>
    </div>

    <div v-else-if="parsedTranscript.length === 0" class="transcript-empty">
      <p>No transcript content available.</p>
    </div>

    <div v-else class="transcript-messages">
      <div
        v-for="(segment, index) in parsedTranscript"
        :key="index"
        class="transcript-message"
        :class="segment.role.toLowerCase().replace(' ', '-')"
      >
        <span class="role-label">{{ segment.role }}:</span>
        <span class="message-text">{{ segment.text }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { LoadingBars } from "@/components/ui";
import { useTranscriptQuery } from "@/composables/ui/useTranscriptQuery";
import { parseTranscript } from "@/utils/callRoles";
import { computed } from "vue";

const props = withDefaults(
  defineProps<{
    transcriptText?: string;
    transcriptUrl?: string | null;
  }>(),
  { transcriptText: "", transcriptUrl: null }
);

const emit = defineEmits<{ refresh: [] }>();

const transcriptUrlRef = computed(() => props.transcriptUrl ?? null);

const { data: fetchedText, isLoading, error, refetch } = useTranscriptQuery(transcriptUrlRef);

// expired presigned URL → parent must re-fetch the CallLog to mint a new one
const isExpiredError = computed(() => {
  const e = error.value;
  return e instanceof Error && /expired/i.test(e.message);
});

function handleRetry() {
  if (isExpiredError.value) {
    emit("refresh");
  } else {
    refetch();
  }
}

const errorMessage = computed(() => {
  if (!error.value) return "";
  if (error.value instanceof Error) return error.value.message;
  return String(error.value);
});

const effectiveText = computed(() => {
  if (props.transcriptUrl) {
    return fetchedText.value ?? "";
  }
  return props.transcriptText ?? "";
});

const parsedTranscript = computed(() => {
  const text = effectiveText.value;
  if (!text) return [];
  return parseTranscript(text);
});
</script>

<style scoped>
.transcript-viewer {
  max-height: 400px;
  overflow-y: auto;
  padding: 14px 16px;
  background: #fafafa;
  border-radius: 6px;
}

.transcript-state,
.transcript-empty {
  text-align: center;
  padding: 40px 20px;
  color: #646464;
  font-size: 0.85rem;
}

.transcript-error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.transcript-error-state .error-message {
  color: #dc2626;
  margin: 0;
  font-size: 0.85rem;
}

.transcript-messages {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.transcript-message {
  font-size: 0.85rem;
  line-height: 1.6;
  padding: 6px 0;
  background: transparent;
  border-left: none;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.transcript-message.customer {
  border-left-color: #0066cc;
  text-align: left;
}

.transcript-message.sales-rep {
  border-left-color: #cc6600;
  text-align: right;
}

.role-label {
  font-weight: 600;
  margin-right: 8px;
}

.transcript-message.customer .role-label {
  color: #0066cc;
}

.transcript-message.sales-rep .role-label {
  color: #cc6600;
}

.message-text {
  color: #2c2c2e;
  word-wrap: break-word;
  overflow-wrap: break-word;
  display: inline-block;
  max-width: 100%;
}

.transcript-viewer::-webkit-scrollbar {
  width: 6px;
}

.transcript-viewer::-webkit-scrollbar-track {
  background: #f5f5f5;
  border-radius: 3px;
}

.transcript-viewer::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.transcript-viewer::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}
</style>
